import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { todayResult as mockTodayResult, overviewData } from '../mockData';
import { ChevronRight } from 'lucide-react';
import axios from 'axios';
import { updatePageSEO } from '../utils/seo';
import StructuredData from '../components/StructuredData';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Home = () => {
  const [todayResult, setTodayResult] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    // Update SEO for homepage with improved targeting
    updatePageSEO({
      path: '/',
      title: 'Morning Sunday Teer Result Today | FR & SR Live Result',
      description: 'Check Morning Sunday Teer Result Today with latest FR 10:30 AM and SR 11:30 AM results, previous results and Shillong Morning Teer updates.',
      robots: 'index, follow'
    });
    
    fetchTodayResult();
  }, []);

  const fetchTodayResult = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/results/today`);
      setTodayResult(response.data);
      setError(null);
    } catch (err) {
      console.error('Error fetching today result:', err);
      // Show today's date with placeholder if no result exists
      const today = new Date().toLocaleDateString('en-GB');
      setTodayResult({
        date: today,
        firstRound: { number: '--', time: '10:30 AM', status: 'pending' },
        secondRound: { number: '--', time: '11:30 AM', status: 'pending' }
      });
      setError(null);
    } finally {
      setLoading(false);
    }
  };
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Structured Data */}
      <StructuredData type="website" />
      {/* Hero Section */}
      <div className="bg-white py-6 sm:py-8 border-b">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-2 leading-tight">
            Morning Sunday Teer Result Today
          </h1>
          <p className="text-lg sm:text-xl text-gray-600">
            FR & SR Live Results | Shillong Morning Teer
          </p>
        </div>
      </div>

      {/* Welcome Banner */}
      <div className="max-w-4xl mx-auto px-4 mt-6 sm:mt-8">
        <div className="bg-gray-900 text-white text-center py-3 sm:py-4 rounded-xl shadow-lg">
          <p className="text-base sm:text-lg font-medium px-2">Welcome to Morning Sunday Teer Results - Check Today's Latest FR & SR Updates</p>
        </div>
      </div>

      {/* Result Card - 16:9 Aspect Ratio */}
      <div className="max-w-6xl mx-auto px-4 mt-6 sm:mt-8">
        <div className="bg-white rounded-3xl shadow-2xl overflow-hidden border-4 border-coral-600">
          {/* Header */}
          <div className="bg-coral-600 text-white text-center py-4 sm:py-5">
            <h3 className="text-xl sm:text-2xl md:text-3xl font-bold uppercase tracking-wider px-4">Morning Sunday Teer Result</h3>
          </div>

          {/* Content Area with White Background - 16:9 layout */}
          <div className="bg-white p-4 sm:p-6 md:p-8">
            {/* Loading State */}
            {loading && !todayResult && (
              <div className="text-center py-12">
                <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-coral-600 border-t-transparent"></div>
                <p className="mt-4 text-gray-600 text-lg">Loading today's result...</p>
              </div>
            )}

            {/* Error State */}
            {error && !todayResult && (
              <div className="text-center py-12">
                <p className="text-red-600 text-lg font-semibold">{error}</p>
                <button 
                  onClick={fetchTodayResult}
                  className="mt-4 px-6 py-2 bg-coral-600 text-white rounded-lg hover:bg-coral-700"
                >
                  Retry
                </button>
              </div>
            )}

            {/* Result Display */}
            {todayResult && (
              <>
                {/* Date */}
                <div className="text-center mb-6 sm:mb-8">
                  <p className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900">Date: {todayResult.date}</p>
                </div>

                {/* Results - Horizontal Layout for 16:9 */}
                <div className="grid grid-cols-2 gap-4 sm:gap-8 mb-6 sm:mb-8 relative">
                  {/* First Round */}
                  <div className="text-center">
                    <p className="text-sm sm:text-base md:text-lg font-bold text-gray-800 mb-3 sm:mb-4 uppercase">F/R ({todayResult.firstRound.time})</p>
                    <div className="inline-flex items-center justify-center w-24 h-24 sm:w-28 sm:h-28 md:w-32 md:h-32 rounded-full border-4 border-coral-600 bg-white shadow-lg">
                      <span className="text-4xl sm:text-5xl md:text-6xl font-black text-gray-900">{todayResult.firstRound.number}</span>
                    </div>
                  </div>

                  {/* Vertical Divider */}
                  <div className="absolute left-1/2 top-0 bottom-0 w-px bg-gray-300 transform -translate-x-1/2"></div>

                  {/* Second Round */}
                  <div className="text-center">
                    <p className="text-sm sm:text-base md:text-lg font-bold text-gray-800 mb-3 sm:mb-4 uppercase">S/R ({todayResult.secondRound.time})</p>
                    <div className="inline-flex items-center justify-center w-24 h-24 sm:w-28 sm:h-28 md:w-32 md:h-32 rounded-full border-4 border-coral-600 bg-white shadow-lg">
                      <span className="text-4xl sm:text-5xl md:text-6xl font-black text-gray-900">{todayResult.secondRound.number}</span>
                    </div>
                  </div>
                </div>

                {/* Check Previous Results Button */}
                <div className="mt-4 sm:mt-6">
                  <Link
                    to="/previous-results"
                    className="block w-full bg-gray-900 hover:bg-gray-800 text-white text-center py-3 sm:py-4 md:py-5 rounded-full font-bold transition-colors text-base sm:text-lg md:text-xl"
                  >
                    Check Previous Results
                  </Link>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Quick Links Grid - Small Compact Boxes */}
      <div className="max-w-6xl mx-auto px-4 mt-4">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4 md:gap-5">
          {/* Common Number Card - Small */}
          <Link to="/common-numbers" className="group relative overflow-hidden rounded-xl shadow-md hover:shadow-lg transition-all border-2 border-coral-600">
            <div className="relative h-28 sm:h-32 md:h-36 bg-coral-600">
              <div className="absolute inset-0 flex flex-col items-center justify-center p-2">
                <div className="w-10 h-10 sm:w-12 sm:h-12 md:w-14 md:h-14 rounded-full border-2 sm:border-3 border-gray-900 bg-coral-500 flex items-center justify-center mb-1 sm:mb-2 relative">
                  <span className="text-xl sm:text-2xl md:text-3xl font-black text-gray-900">C</span>
                  <div className="absolute -top-1 -right-1 w-4 h-4 sm:w-5 sm:h-5 bg-yellow-400 rounded-full flex items-center justify-center">
                    <svg className="w-2.5 h-2.5 sm:w-3 sm:h-3 text-gray-900" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M11 3a1 1 0 10-2 0v1a1 1 0 102 0V3zM15.657 5.757a1 1 0 00-1.414-1.414l-.707.707a1 1 0 001.414 1.414l.707-.707zM18 10a1 1 0 01-1 1h-1a1 1 0 110-2h1a1 1 0 011 1zM5.05 6.464A1 1 0 106.464 5.05l-.707-.707a1 1 0 00-1.414 1.414l.707.707zM5 10a1 1 0 01-1 1H3a1 1 0 110-2h1a1 1 0 011 1zM8 16v-1h4v1a2 2 0 11-4 0zM12 14c.015-.34.208-.646.477-.859a4 4 0 10-4.954 0c.27.213.462.519.476.859h4.002z"/>
                    </svg>
                  </div>
                </div>
                <h3 className="text-[10px] sm:text-xs md:text-sm font-bold text-white drop-shadow-md text-center">Common Number</h3>
                <p className="text-[7px] sm:text-[8px] text-white/70 mt-0.5 sm:mt-1">© morningsandayteer.com</p>
              </div>
            </div>
          </Link>

          {/* Dream Number Card */}
          <Link to="/dream-number" className="group relative overflow-hidden rounded-xl shadow-md hover:shadow-lg transition-all border-2 border-coral-600">
            <div className="relative h-28 sm:h-32 md:h-36 bg-coral-500">
              <div className="absolute inset-0 flex flex-col items-center justify-center p-2">
                {/* Bed/Sleep Icon */}
                <div className="w-12 h-12 sm:w-16 sm:h-16 mb-2">
                  <svg viewBox="0 0 100 100" className="w-full h-full text-gray-900" fill="currentColor">
                    {/* Bed Frame */}
                    <rect x="15" y="50" width="70" height="35" fill="none" stroke="currentColor" strokeWidth="4" rx="2"/>
                    {/* Pillow/Thought Bubble */}
                    <path d="M 65 20 Q 70 15, 75 20 Q 80 25, 75 30 Q 70 35, 65 30 Q 60 25, 65 20 Z" fill="currentColor" opacity="0.8"/>
                    {/* Small bubble */}
                    <circle cx="60" cy="38" r="3" fill="currentColor" opacity="0.6"/>
                    <circle cx="55" cy="44" r="2" fill="currentColor" opacity="0.5"/>
                    {/* Person sleeping */}
                    <circle cx="45" cy="60" r="8" fill="currentColor"/>
                    <path d="M 37 68 L 37 75 L 40 75 L 40 68 Z" fill="currentColor"/>
                    <path d="M 50 68 L 50 75 L 53 75 L 53 68 Z" fill="currentColor"/>
                    <rect x="38" y="65" width="15" height="8" fill="currentColor"/>
                  </svg>
                </div>
                <h3 className="text-sm sm:text-base md:text-lg font-bold text-white drop-shadow-md text-center">Dream Number</h3>
                <div className="flex items-center justify-center gap-1 mt-1">
                  <div className="w-1 h-1 rounded-full bg-white"></div>
                  <div className="w-1 h-1 rounded-full bg-white"></div>
                  <div className="w-1 h-1 rounded-full bg-white"></div>
                </div>
                <p className="text-[7px] sm:text-[8px] text-white/70 mt-0.5">© morningsandayteer.com</p>
              </div>
            </div>
          </Link>

          {/* Previous Result Card - Small */}
          <Link to="/previous-results" className="group relative overflow-hidden rounded-xl shadow-md hover:shadow-lg transition-all border-2 border-teal-500">
            <div className="relative h-28 sm:h-32 md:h-36 bg-teal-500">
              <div className="absolute inset-0 flex flex-col items-center justify-center p-2">
                <div className="relative w-10 h-10 sm:w-12 sm:h-12 md:w-14 md:h-14 mb-1">
                  <svg className="w-full h-full text-white drop-shadow-md" fill="currentColor" viewBox="0 0 24 24">
                    <circle cx="12" cy="12" r="10" fill="white" opacity="0.95"/>
                    <circle cx="12" cy="12" r="7" fill="#14b8a6" opacity="0.8"/>
                    <circle cx="12" cy="12" r="4" fill="white" opacity="0.95"/>
                    <circle cx="12" cy="12" r="1.5" fill="#dc2626"/>
                  </svg>
                  <div className="absolute top-0 left-1/2 transform -translate-x-1/2 -translate-y-1">
                    <svg className="w-3 h-6 sm:w-4 sm:h-8 md:w-5 md:h-10 text-red-600 drop-shadow-md" viewBox="0 0 24 48" fill="currentColor">
                      <path d="M12 0 L14 40 L12 48 L10 40 Z"/>
                      <path d="M12 40 L8 44 L12 48 L16 44 Z"/>
                    </svg>
                  </div>
                </div>
                <h3 className="text-[10px] sm:text-xs md:text-sm font-bold text-white drop-shadow-md uppercase text-center tracking-wide" style={{fontFamily: 'Impact, sans-serif'}}>
                  Previous Result
                </h3>
                <p className="text-[7px] sm:text-[8px] text-white/70 mt-0.5 sm:mt-1">© morningsandayteer.com</p>
              </div>
            </div>
          </Link>

          {/* House Target Card - Small */}
          <Link to="/house-target" className="group relative overflow-hidden rounded-xl shadow-md hover:shadow-lg transition-all border-2 border-amber-500">
            <div className="relative h-28 sm:h-32 md:h-36 bg-amber-500">
              <div className="absolute inset-0 flex flex-col items-center justify-center p-2">
                <div className="w-10 h-10 sm:w-12 sm:h-12 md:w-14 md:h-14 rounded-lg border-2 sm:border-3 border-gray-900 bg-amber-400 flex items-center justify-center mb-1 sm:mb-2 relative">
                  <span className="text-xl sm:text-2xl md:text-3xl font-black text-gray-900">H</span>
                  <div className="absolute -top-1 -right-1 w-4 h-4 sm:w-5 sm:h-5 bg-red-500 rounded-full flex items-center justify-center">
                    <svg className="w-2.5 h-2.5 sm:w-3 sm:h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M10 2a8 8 0 100 16 8 8 0 000-16zm1 11H9v-2h2v2zm0-4H9V5h2v4z"/>
                    </svg>
                  </div>
                </div>
                <h3 className="text-[10px] sm:text-xs md:text-sm font-bold text-white drop-shadow-md text-center">House Target</h3>
                <p className="text-[7px] sm:text-[8px] text-white/70 mt-0.5 sm:mt-1">© morningsandayteer.com</p>
              </div>
            </div>
          </Link>
        </div>
      </div>

      {/* SEO Content Section */}
      <div className="max-w-4xl mx-auto px-4 mt-12 mb-16">
        {/* Main Content */}
        <div className="prose prose-lg max-w-none">
          {/* Section 1: Morning Sunday Teer Result Today */}
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4 border-b-2 border-coral-600 pb-2">
            Morning Sunday Teer Result Today
          </h2>
          <p className="text-gray-700 leading-relaxed mb-4">
            The Morning Sunday Teer Result is announced daily based on the archery competition held in Shillong, Meghalaya. This website provides quick access to today's results, including both First Round (FR) and Second Round (SR) numbers. Visitors can check the latest numbers as soon as they are declared, ensuring you never miss an update.
          </p>
          <p className="text-gray-700 leading-relaxed mb-6">
            Unlike computer-generated lotteries, Morning Sunday Teer uses a traditional archery-based system where trained archers shoot arrows at a target. Officials count the number of arrows that hit the target, and the last two digits of this count determine the winning number for each round.
          </p>

          {/* Section 2: FR & SR Timing */}
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4 mt-8 border-b-2 border-coral-600 pb-2">
            Morning Sunday Teer FR & SR Time
          </h2>
          <p className="text-gray-700 leading-relaxed mb-4">
            The Morning Sunday Teer results are announced at specific times each day:
          </p>
          <ul className="list-disc list-inside mb-4 text-gray-700 space-y-2">
            <li><strong>First Round (FR):</strong> Results declared at <strong>10:30 AM</strong> daily</li>
            <li><strong>Second Round (SR):</strong> Results declared at <strong>11:30 AM</strong> daily</li>
          </ul>
          <p className="text-gray-700 leading-relaxed mb-6">
            These timings are consistent throughout the week. Players and enthusiasts wait for these specific times to check if their predicted numbers match the declared results. The archery competition takes place earlier in the morning, and after officials complete the count, results are published promptly.
          </p>

          {/* Section 3: Previous Results */}
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4 mt-8 border-b-2 border-coral-600 pb-2">
            Previous Morning Sunday Teer Results
          </h2>
          <p className="text-gray-700 leading-relaxed mb-4">
            Understanding past result patterns can help players make informed predictions. Our dedicated <Link to="/previous-results" className="text-coral-600 hover:text-coral-700 font-semibold underline">Previous Results page</Link> provides a comprehensive archive of Morning Sunday Teer results dating back to 2020.
          </p>
          <p className="text-gray-700 leading-relaxed mb-6">
            You can browse through historical data, compare FR and SR numbers across different dates, and analyze trends. Many players use this information alongside other prediction tools like <Link to="/common-numbers" className="text-coral-600 hover:text-coral-700 font-semibold underline">Common Numbers</Link> and <Link to="/house-target" className="text-coral-600 hover:text-coral-700 font-semibold underline">House Target</Link> to develop their strategies.
          </p>

          {/* Section 4: About Morning Sunday Teer */}
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4 mt-8 border-b-2 border-coral-600 pb-2">
            About Morning Sunday Teer
          </h2>
          <p className="text-gray-700 leading-relaxed mb-4">
            Morning Sunday Teer is an archery-based number game popular in Meghalaya and surrounding regions of Northeast India. The game has deep cultural roots and has been played for decades, combining traditional archery skills with a number prediction format.
          </p>
          <p className="text-gray-700 leading-relaxed mb-4">
            The process involves skilled archers shooting arrows at a designated target during morning hours. After each round of shooting, officials count the total number of arrows that successfully hit the target. The last two digits of this count become the winning number for that particular round.
          </p>
          <p className="text-gray-700 leading-relaxed mb-6">
            The "Sunday" in Morning Sunday Teer refers to the specific game variant, not the day of the week. This game runs daily and is part of the broader Teer gaming culture in the region. Players can also explore <Link to="/dream-number" className="text-coral-600 hover:text-coral-700 font-semibold underline">Dream Numbers</Link> for additional prediction insights based on traditional interpretations.
          </p>

          {/* Section 5: How to Check Results */}
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4 mt-8 border-b-2 border-coral-600 pb-2">
            How to Check Morning Sunday Teer Results
          </h2>
          <p className="text-gray-700 leading-relaxed mb-4">
            Checking today's Morning Sunday Teer Result on this website is straightforward:
          </p>
          <ol className="list-decimal list-inside mb-4 text-gray-700 space-y-2">
            <li>Visit the homepage at the announced result times (10:30 AM for FR, 11:30 AM for SR)</li>
            <li>Today's date and corresponding FR/SR numbers will be displayed in the result card</li>
            <li>If a result is not yet declared, you will see "--" indicating pending status</li>
            <li>Refresh the page after the scheduled time to view updated results</li>
          </ol>
          <p className="text-gray-700 leading-relaxed mb-6">
            This website is an independent informational platform providing quick access to publicly announced results. We update the numbers as soon as official declarations are made, ensuring accuracy and timeliness for all visitors.
          </p>

          {/* Section 6: Understanding FR and SR */}
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4 mt-8 border-b-2 border-coral-600 pb-2">
            Understanding FR and SR Numbers
          </h2>
          <p className="text-gray-700 leading-relaxed mb-4">
            The Morning Sunday Teer result consists of two separate rounds:
          </p>
          <p className="text-gray-700 leading-relaxed mb-4">
            <strong>First Round (FR):</strong> This is the result of the first archery session conducted in the morning. Archers shoot a predetermined number of arrows, and the count of successful hits determines the FR number. This number typically consists of two digits.
          </p>
          <p className="text-gray-700 leading-relaxed mb-6">
            <strong>Second Round (SR):</strong> Following the first round, a second archery session takes place. The same counting method applies, and the resulting number forms the SR. Both FR and SR are independent of each other and are based purely on the arrow count from their respective rounds.
          </p>

          {/* Disclaimer */}
          <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mt-8 rounded">
            <p className="text-sm text-gray-700 leading-relaxed">
              <strong>Disclaimer:</strong> This is an independent informational website. We are not officially affiliated with any Teer organizing authority. All results displayed here are based on publicly available information. Users are advised to verify results from official sources and participate responsibly.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Home;
